import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import {
  Avatar,
  Box,
  Button,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { graphql } from "babel-plugin-relay/macro";
import dayjs from "dayjs";
import { useContext, useMemo } from "react";
import { useFragment } from "react-relay";
import { useLocation, useNavigate } from "react-router-dom";
import { NotificationTableRow_notification$key } from "./__generated__/NotificationTableRow_notification.graphql";
import CDPAudienceInfoTableCell from "../../../../components/CDPAudienceInfoTableCell";
import UTMCampaignTableCell from "../../../../components/UTMCampaignTableCell";
import DebugContext from "../../../../contexts/DebugContext";
import { DialogType } from "../NotificationsTab";
import NotificationPublishButton from "./NotificationPublishButton";
import NotificationStatusPill from "./NotificationStatusPill";
import useNavigateToEditNotification from "./hooks/useNavigateToEditNotification";

const notifFragment = graphql`
  fragment NotificationTableRow_notification on GraphQLNotification {
    id
    uuid
    audienceLanguages
    automaticArchiveTimestamp
    body
    confirmationStatus
    thumbnailUrl
    externalThumbnailUri
    isArchived
    landingPath
    timestamp
    title
    utmCampaign
    audiences {
      audienceName
    }
    dailyDeal {
      id
    }
    feedPost {
      id
    }
    highlight {
      id
      highlightAlbum {
        id
      }
    }
  }
`;

const NotificationTableRow = ({
  isNew,
  notification: notificationKey,
  onOpenDialog,
  hidePastPublished = false,
}: {
  isNew: boolean;
  notification: NotificationTableRow_notification$key;
  onOpenDialog: (dialogType: DialogType) => void;
  hidePastPublished?: boolean;
}) => {
  const { debug } = useContext(DebugContext);

  const notification = useFragment(notifFragment, notificationKey);
  const automaticArchiveTimestamp =
    notification.automaticArchiveTimestamp ?? null;
  const publishedTime = notification.timestamp;
  const dailyDealID = notification.dailyDeal?.id;
  const feedPostID = notification.feedPost?.id;
  const highlightID = notification.highlight?.id;
  const isLinked =
    dailyDealID != null || feedPostID != null || highlightID != null;
  const isScheduled = dayjs(publishedTime).isAfter(dayjs());
  const hasAutoArchiveTime = automaticArchiveTimestamp !== null;
  const isAutoArchived =
    hasAutoArchiveTime && dayjs().isAfter(dayjs(automaticArchiveTimestamp));
  const isArchived = notification.isArchived;
  const navigateToEditNotification = useNavigateToEditNotification();
  const navigate = useNavigate();
  const location = useLocation();

  const debugComponent = (
    <Box
      sx={{
        position: "absolute",
        top: -6,
        left: -16,
        bgcolor: "rgb(20,184,166, 0.3)",
        fontSize: "8px",
      }}
    >
      <p>
        <b>PK:</b> {window.atob(notification.id).split(":")[1]}
      </p>
      <p>
        <b>UUID:</b> {notification.uuid}
      </p>
      <p>
        <b>URL:</b> {notification.landingPath}
      </p>
    </Box>
  );
  const theme = useTheme();

  const [datePublished, canPublish] = useMemo(() => {
    if (isScheduled) {
      return [dayjs(publishedTime).format("llll z"), true];
    }
    if (hidePastPublished) {
      return ["--", true];
    }
    return [dayjs().to(dayjs(publishedTime)), false];
  }, [hidePastPublished, isScheduled, publishedTime]);

  const notificationItem = (
    <ListItem>
      {debug && debugComponent}
      <ListItemAvatar>
        <Avatar
          alt=""
          src={
            notification?.thumbnailUrl ??
            notification?.externalThumbnailUri ??
            undefined
          }
          variant="rounded"
          sx={{ bgcolor: theme.palette.info.main, color: "black" }}
        >
          <NotificationsNoneIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText
        primary={
          <Typography
            variant="body2"
            color={"black"}
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "1",
              WebkitBoxOrient: "vertical",
            }}
          >
            {notification.title}
          </Typography>
        }
        secondary={
          <Typography
            variant="body2"
            sx={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              display: "-webkit-box",
              WebkitLineClamp: "1",
              WebkitBoxOrient: "vertical",
            }}
          >
            {notification.body}
          </Typography>
        }
      />
    </ListItem>
  );

  const editButton =
    isScheduled || notification.confirmationStatus === "pending" ? (
      <Button
        size="small"
        variant="text"
        color="secondary"
        onClick={() => {
          navigateToEditNotification(notification);
        }}
      >
        {feedPostID != null
          ? "Edit in Feed"
          : highlightID != null
          ? "Edit in Highlights"
          : dailyDealID != null
          ? "Edit in Daily Deals"
          : "Edit"}
      </Button>
    ) : null;
  const archiveOrDeleteButton =
    !isLinked &&
    (isScheduled || notification.confirmationStatus === "pending") ? (
      <Button
        size="small"
        variant="text"
        color="error"
        onClick={() => onOpenDialog(DialogType.DELETE)}
      >
        Delete
      </Button>
    ) : !isScheduled ? (
      <Button
        size="small"
        variant="text"
        color="error"
        onClick={() => onOpenDialog(DialogType.ARCHIVE)}
      >
        Archive
      </Button>
    ) : null;

  return (
    <TableRow
      key={notification.id}
      sx={{
        background: isNew ? "rgba(0,0,0, 0.08)" : "white",
        transition: "background .2s ease-in",
      }}
    >
      <TableCell align="center">{notificationItem}</TableCell>
      <CDPAudienceInfoTableCell item={notification} />
      <UTMCampaignTableCell utmCampaign={notification.utmCampaign} />
      <TableCell align="center">
        <NotificationStatusPill notification={notification} />
      </TableCell>
      <TableCell align="center">
        <Tooltip
          title={
            datePublished === "--"
              ? null
              : dayjs(publishedTime).format("llll z")
          }
          placement="top"
          arrow
        >
          <Box>{datePublished}</Box>
        </Tooltip>
      </TableCell>
      <TableCell align="center">
        <Stack direction="row" justifyContent="end" flexShrink={0}>
          {!(isArchived || isAutoArchived) && (
            <>
              {archiveOrDeleteButton}
              {editButton}
              {canPublish && (
                <NotificationPublishButton notification={notification} />
              )}
            </>
          )}
          {!canPublish && (
            <Button
              size="small"
              variant="text"
              color="secondary"
              onClick={() => {
                navigate(location.pathname + "/create", {
                  state: {
                    resendID: notification.id,
                  },
                });
              }}
            >
              Re-send
            </Button>
          )}
        </Stack>
      </TableCell>
    </TableRow>
  );
};

export default NotificationTableRow;
